<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue';
import { auth0 } from '@/services/auth0';
import { useContextStore, useTenantStore } from '@/store';

import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useRoute } from 'vue-router';
import {
  SwatchIcon,
  WindowIcon,
  PencilIcon,
  CheckBadgeIcon,
  ScaleIcon,
  PencilSquareIcon,
} from '@heroicons/vue/24/solid'
import { CheckIcon } from '@heroicons/vue/20/solid'
import type { AdminRole } from "@upclaim/sdk/src/extra-types";
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';

import adminAPI from '@/services/api';
import { TenantDetails } from '@/interfaces';
import { MessageCircleQuestion } from 'lucide-vue-next';
import { asyncComputed } from '@vueuse/core';

const evaluating = ref(false)
const tenant = asyncComputed(async () =>{
  return useTenantStore().tenant || useTenantStore().retrieveCurrentTenant()
}, null, evaluating);

const stepsOrder = {
  "DOMAIN": 0,
  "LEGAL_ASSETS": 1,
  "SIGNATURE": 2,
  "CLAIM_VALIDATION": 3,
};
const currentStep = computed(() => tenant.value?.tenantOnboarding?.currentStep?.step || "DOMAIN");

const timeline = computed(() => [
  {
    id: 0,
    content: 'Configuration du',
    target: 'Nom de domaine',
    href: '#',
    date: null,//'En cours...',
    datetime: '2020-09-20',
    icon: WindowIcon,
    iconBackground: 'bg-blue-500',
    step: "DOMAIN",
  },
  {
    id: 1,
    content: 'Ajout du',
    target: 'Branding de l\'agence',
    href: '#',
    date: null,
    datetime: '2020-09-20',
    icon: SwatchIcon,
    iconBackground: 'bg-blue-500',
    step: "LEGAL_ASSETS",
  },
  {
    id: 2,
    content: 'Flux de',
    target: 'Signature Electronique',
    href: '#',
    date: null,
    datetime: '2020-09-22',
    icon: PencilSquareIcon,
    iconBackground: 'bg-blue-500',
    step: "SIGNATURE",
  },
  {
    id: 3,
    content: 'Validation du',
    target: "Premier dossier",
    href: '#',
    date: null,
    datetime: '2020-09-28',
    icon: CheckBadgeIcon,
    iconBackground: 'bg-green-500',
    step: "CLAIM_VALIDATION",
  },
])

function stepStatus(stepName) {
  return {
    current: currentStep.value === stepName,
    upcoming: stepsOrder[stepName] > stepsOrder[currentStep.value],
    completed: stepsOrder[stepName] < stepsOrder[currentStep.value],
  }
}
</script>

<template>
<div class="flex w-full h-screen text-left">
  <div class="fixed relative hidden w-5/12 h-screen p-24 border-r lg:flex lg:flex-shrink-0 bg-gradient-to-r from-zinc-50 to-zinc-100 border-zinc-200">
    <div class="absolute top-6 left-6">
      <img src="/logo_blue.svg" class="w-10 " />
        <!-- <img src="https://ucarecdn.com/5cbc0f08-7c4c-4fff-a1e7-0854351470b7/" class=" w-28" /> -->
    </div>

    <!-- Vertical flex -->
    <div class="flex flex-col w-full h-full place-content-between">
      <!-- 1. LOGO -->
      <div>
        <!-- <img src="https://ucarecdn.com/5cbc0f08-7c4c-4fff-a1e7-0854351470b7/" class=" w-36" /> -->
        <h2 class="text-base italic leading-7 text-slate-500">Complétez les étapes suivantes pour configurer votre Agence de Voyages avec Upclaim</h2>
      </div>

      <!-- 2. TIMELINE -->
      <div class="py-16">
      <div class="flex flex-col">
        <div class="flow-root p-0">
          <ul role="list" class="-mb-8">
            <li v-for="(event, eventIdx) in timeline" :key="event.id">
              <div class="relative pb-20">
                <span v-if="eventIdx !== timeline.length - 1" class="absolute left-6 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                <div class="relative flex space-x-6">
                  <div>
                    <span :class="[stepStatus(event.step).current && 'bg-blue-500', stepStatus(event.step).upcoming && 'bg-slate-300', stepStatus(event.step).completed && 'bg-green-500', 'flex shadow-lg h-12 w-12 border-4 items-center justify-center rounded-full ring-8 ring-white']">
                      <component :is="stepStatus(event.step).completed ? CheckIcon : event.icon" class="text-white w-7 h-7" aria-hidden="true" />
                    </span>
                  </div>
                  <div class="flex justify-between flex-1 min-w-0 pt-2.5 pl-1 space-x-4">
                    <div>
                      <p class="text-base text-gray-700">
                        {{ event.content }} <a :href="event.href" class="font-medium text-gray-900">{{ event.target }}</a>
                      </p>
                    </div>
                    <div class="text-base text-right text-gray-500 whitespace-nowrap">
                      <time :datetime="event.datetime">{{ event.date }}</time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

      <!-- 3. HELP CTA BUTTON -->
      <div class="mx-auto">
        <BaseButton text="Besoin d'aide ?" size="lg" :icon="MessageCircleQuestion" />
      </div>
    </div>
  </div>
  <main v-if="tenant" class="relative z-0 flex flex-1 p-24 overflow-y-auto">
    <div>
      <span class="inline-flex items-center px-2 py-1 mb-8 text-xs font-medium text-purple-700 rounded-md bg-purple-50 ring-1 ring-inset ring-purple-700/10">
      Étape {{ stepsOrder[currentStep] + 1 }} sur 4
    </span>
    <span v-if="tenant.tenantBranding?.postmarkappDomainId" class="ml-4 inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
      <svg class="h-1.5 w-1.5 fill-blue-500" viewBox="0 0 6 6" aria-hidden="true">
        <circle cx="3" cy="3" r="3" />
      </svg>
      Domaine :<span class="font-semibold">{{ tenant.tenantBranding?.subdomainUri }}</span>
    </span>
    <RouterView v-slot="{ Component }">
      <template v-if="Component">
        <component :is="Component" v-bind="{ tenant }" />
      </template>
    </RouterView>
    </div>
  </main>
</div>
</template>